import { useCallback } from 'react';
import { useTranslation, type UseTranslationOptions } from 'react-i18next';

import type { Namespace } from './i18n.js';

/**
 * Wrapper tipado do `useTranslation` do react-i18next.
 * Uso: `const { t } = useT('auth'); t('login.title')`.
 */
export function useT(ns: Namespace, options?: UseTranslationOptions<undefined>) {
  return useTranslation(ns, options);
}

/** Tipo mínimo de um erro de campo do react-hook-form: só precisamos de `message`. */
export interface FieldErrorLike {
  message?: string | undefined;
}

/**
 * Traduz `error.message` de um campo do react-hook-form quando a mensagem
 * é uma **chave i18n** (ex: `validation:email.invalid` ou `validation.email.invalid`).
 * Retorna `undefined` quando não há erro — pronto para uso condicional.
 *
 * Uso nos componentes:
 *   const tError = useFieldError();
 *   const emailError = tError(errors.email);
 *   {emailError && <p>{emailError}</p>}
 */
export function useFieldError() {
  const { t } = useTranslation();
  return useCallback(
    (error: FieldErrorLike | undefined): string | undefined => {
      if (!error?.message) return undefined;
      return t(error.message);
    },
    [t],
  );
}
